import { useQuery } from '@tanstack/react-query'
import LoadingScreen from 'components/Layout/LoadingScreen'
import type { Campaign } from 'models/campaign'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import {
  CONTACTS_TABLE_KEY,
  getCampaignTableKey,
} from 'constants/tableQueryKeys'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import { CAMPAIGN_DEFAULT_COLUMN_VISIBILITY } from 'components/Tables/ContactsTable/sequence/campaignDefaultColumns'
import apiService from 'services/api'
import { useGetCampaignCompanies } from './campaigns_api'
import { CampaignContactsZeroState } from './CampaignContactsZeroState'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useHasPendingContactRequest } from 'services/contactRequestApi'

interface Props {
  campaign: Campaign
  onSwitchToTargets: () => void
  onContactRequested?: () => void
  onViewContactRequests?: () => void
}

export function CampaignDetailContactsTab({
  campaign,
  onSwitchToTargets,
  onContactRequested,
  onViewContactRequests,
}: Props) {
  const api = apiService()
  const { data: campaignCompanies, isLoading: isLoadingTargets } =
    useGetCampaignCompanies(campaign.id)
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['campaign', campaign.id, 'contacts-count'],
    queryFn: () =>
      api.getContactsList({
        campaign: campaign.id,
        campaign_page: true,
        type: 'all',
        limit: 1,
        page: 1,
      }),
    staleTime: 1000 * 60 * 5,
  })
  const hasContactRequests = campaign.type
    ? ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
    : true
  const targetCount = campaignCompanies?.total_count ?? 0
  const contactCount = contactsData?.count ?? 0
  // Gate only on the synchronously-known campaign type so this runs in parallel
  // with the targets/contacts queries rather than waterfalling behind them. The
  // result is only consumed on the zero-contacts path below.
  const { data: hasPendingContactRequest, isLoading: isLoadingPendingRequest } =
    useHasPendingContactRequest(campaign.id, hasContactRequests)

  if (isLoadingTargets || isLoadingContacts) {
    return <LoadingScreen />
  }

  if (targetCount === 0) {
    return (
      <CampaignContactsZeroState
        variant="assign-targets"
        hasContactRequests={hasContactRequests}
        campaignId={campaign.id}
        isMultiType={!campaign.type}
        onSwitchToTargets={onSwitchToTargets}
        onContactRequested={onContactRequested}
      />
    )
  }

  if (contactCount === 0) {
    if (isLoadingPendingRequest) {
      return <LoadingScreen />
    }
    return (
      <CampaignContactsZeroState
        variant="request-contacts"
        hasContactRequests={hasContactRequests}
        hasPendingContactRequest={hasPendingContactRequest ?? false}
        campaignId={campaign.id}
        isMultiType={!campaign.type}
        onSwitchToTargets={onSwitchToTargets}
        onContactRequested={onContactRequested}
        onViewContactRequests={onViewContactRequests}
      />
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <StageColorProvider value="pipeline">
        <FBPageTableContainer
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#ffffff',
            overflow: 'hidden',
            boxShadow:
              '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
          }}
        >
          <ContactsTable
            disableUrlPagination
            campaignId={campaign.id}
            campaignProductId={campaign.product?.id ?? null}
            defaultColumnVisibility={CAMPAIGN_DEFAULT_COLUMN_VISIBILITY}
            tableKey={getCampaignTableKey(CONTACTS_TABLE_KEY, campaign.id)}
            filterIdentifierModifier={`_CAMPAIGN_V2_${campaign.id}`}
            baseFilters={{ campaign: campaign.id }}
          />
        </FBPageTableContainer>
      </StageColorProvider>
    </TableLinkStyleProvider>
  )
}
